/**
 * Copyright (c) Omnissa, LLC. All rights reserved.
 * This product is protected by copyright and intellectual property laws in the
 * United States and other countries as well as by international treaties.
 * -- Omnissa Restricted
 *
 * This is used to handle the channels and maintain the map
 * at client side.
 *
 * @format
 */
import { Subject } from "rxjs";
import { ChannelDescriptor } from "./vc-client";
import { RDPChan, VirtualChanInfo } from "./vctransport";
import { VCDataGenerator } from "./vc-wire-message";

export class VCChannel {
   private invalid = false;
   private dataGenarator = null;
   constructor(private vchan: VirtualChanInfo) {
      if (!vchan) {
         this.setUnavailable();
         return;
      }
      this.dataGenarator = new VCDataGenerator(this.vchan.channelName);
   }
   public send = (data: Uint8Array) => {
      if (this.invalid || !this.vchan) {
         return;
      }
      const dataPackage = this.dataGenarator.generateNewData(new Uint8Array(data), this.vchan.channelId);
      this.vchan.sendData(dataPackage);
   };
   // for async shutdown without continuing send out date while disconnecting
   public setUnavailable = () => {
      this.invalid = true;
   };
   public getName = () => {
      return this.vchan.channelName;
   };
}

/**
 * Record channels and track the status here.
 * One map is enough for Chrome Client.
 */
export class ChannelManager {
   public $channelConnected = new Subject<ChannelDescriptor>();
   public $channelDisconnected = new Subject<ChannelDescriptor>();
   private channelMap = new Map<string, VCChannel>();
   constructor(
      private sessionKey: string,
      private uuid: string
   ) {
      RDPChan.$registerChannel.subscribe(this.addChannel);
      RDPChan.$unregisterChannel.subscribe(this.removeChannel);
   }
   public getChannel = (channelInfo: ChannelDescriptor): VCChannel => {
      return this.channelMap.get(channelInfo.channelName);
   };
   public addChannel = (channel: VirtualChanInfo) => {
      this.channelMap.set(channel.channelName, new VCChannel(channel));
      const channelDescriptor = {
         channelName: channel.channelName,
         sessionKey: this.sessionKey,
         uuid: this.uuid
      };
      this.$channelConnected.next(channelDescriptor);
   };
   public removeChannel = (channelName: string) => {
      this.channelMap.delete(channelName);
      const channelDescriptor = {
         channelName: channelName,
         sessionKey: this.sessionKey,
         uuid: this.uuid
      };
      this.$channelDisconnected.next(channelDescriptor);
   };
}
